import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { BASEMAP_URL, BASEMAP_ATTRIBUTION } from '../utils/basemap';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { StatusPill, Tone, vehiclePhoto, vehicleTypeImage } from './ui/dg';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Fix pour les icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface OverviewMapProps {
  userPosition?: { lat: number; lng: number };
  vehicles?: Array<{
    id: number;
    title: string;
    type: string;
    latitude: number;
    longitude: number;
    is_available: boolean;
    hourly_rate: number;
    // Optionnels — utilisés par la carte détaillée en mode 'select' (Flotte)
    year?: string | number;
    city?: string | null;
    isPlatformOwned?: boolean;
    // Popup du mode 'select' : la fiche d'origine (pour sa photo), la plaque, le
    // type en français, l'état de l'annonce, les tarifs et le propriétaire.
    raw?: any;
    plate?: string | null;
    typeLabel?: string;
    state?: { label: string; tone: Tone };
    prices?: { value: string; unit: string }[];
    ownerName?: string | null;
  }>;
  // Malgré son nom, ceci est la VALEUR du véhicule sur laquelle centrer la
  // carte (voir VehicleCenter plus bas) — pas un callback de clic. Le nom est
  // resté pour ne pas casser les appels existants (OverviewView).
  onVehicleSelect?: any;
  // Mode 'select' remplace la navigation vers /booking par un callback — utile
  // pour un contexte admin (ex: Flotte) où le clic doit ouvrir une fiche de
  // gestion plutôt que le parcours de réservation d'un locataire.
  mode?: 'book' | 'select';
  onVehicleClick?: (vehicle: any) => void;
  actionLabel?: string;
  markerColor?: string;
  // Prioritaire sur markerColor quand fourni — permet une couleur par
  // véhicule (ex: une couleur par type dans Flotte) plutôt qu'une seule
  // couleur pour tous les marqueurs.
  getMarkerColor?: (vehicle: any) => string;
  // Itinéraire à tracer (Melqarto) : le tracé en [lat, lng], son point
  // d'arrivée, et l'étiquette au survol. La carte se cadre dessus.
  route?: {
    points: [number, number][];
    end: { lat: number; lng: number };
    label?: string;
  };
}

/** Photo de l'annonce sur toute la largeur du popup, à défaut le rendu du type. */
const PopupPhoto: React.FC<{ vehicle: any }> = ({ vehicle }) => {
  const [broken, setBroken] = React.useState(false);
  const src = vehiclePhoto(vehicle.raw);
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="h-full w-full object-cover" />;
  }
  return (
    <div className="flex h-full w-full items-center justify-center">
      <img src={vehicleTypeImage(vehicle.type)} alt="" className="h-20" />
    </div>
  );
};

/** Tracé d'itinéraire, avec cadrage sur toute sa longueur quand il change. */
const RouteOverlay: React.FC<{ route: NonNullable<OverviewMapProps['route']>; color: string }> = ({ route, color }) => {
  const map = useMap();
  useEffect(() => {
    if (route.points.length < 2) return;
    map.fitBounds(L.latLngBounds(route.points), { padding: [40, 40], maxZoom: 16 });
  }, [map, route]);

  return (
    <>
      {/* Liseré blanc sous le tracé : lisible sur les rues comme sur les parcs */}
      <Polyline positions={route.points} pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.9 }} />
      <Polyline positions={route.points} pathOptions={{ color, weight: 4, opacity: 0.95 }}>
        {route.label && <Tooltip sticky>{route.label}</Tooltip>}
      </Polyline>
      <CircleMarker
        center={[route.end.lat, route.end.lng]}
        radius={7}
        pathOptions={{ color: '#ffffff', weight: 3, fillColor: color, fillOpacity: 1 }}
      >
        <Tooltip direction="top" offset={[0, -8]}>Restitution</Tooltip>
      </CircleMarker>
    </>
  );
};

const hexToRgba = (hex: string, alpha: number) => {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Composant pour centrer la carte sur la position de l'utilisateur
const MapCenter: React.FC<{ position: { lat: number; lng: number } }> = ({ position }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView([position.lat, position.lng], 13);
  }, [map, position]);
  
  return null;
};

// Leaflet fige la taille de ses tuiles au montage. Si le conteneur change de
// taille ensuite (ex: bascule Liste/Grille -> Carte pleine largeur dans
// Flotte), la carte reste rendue à son ancienne taille et le reste du
// conteneur apparaît gris — invalidateSize() force Leaflet à recalculer.
const MapResizeHandler: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
};

const VehicleCenter: React.FC<{ vehicle: any }> = ({ vehicle }) => {
  const map = useMap();
  
  useEffect(() => {
    if (vehicle && vehicle.latitude && vehicle.longitude) {
      map.setView([vehicle.latitude, vehicle.longitude], 15);
    }
  }, [map, vehicle]);
  
  return null;
};

// Composant pour les contrôles de carte personnalisés
const MapControls: React.FC<{
  userPosition: { lat: number; lng: number } | null;
  showRecenter?: boolean;
}> = ({ userPosition, showRecenter = true }) => {
  const map = useMap();

  const handleZoomIn = () => {
    map.zoomIn(1, {
      animate: false
    });
  };

  const handleZoomOut = () => {
    map.zoomOut(1, {
      animate: false
    });
  };

  const handleReCenter = () => {
    if (userPosition) {
      map.setView([userPosition.lat, userPosition.lng], 13);
    }
  };

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col space-y-2">
      {/* Bouton de recentrage — sans objet en back-office : pas de "ma position" à recentrer */}
      {showRecenter && (
      <button
        onClick={handleReCenter}
        disabled={!userPosition}
        className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Se recentrer sur ma position"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      </button>
      )}

      {/* Bloc de zoom */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Zoom In */}
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors border-b border-gray-100"
          title="Zoom avant"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
        </button>
        
        {/* Zoom Out */}
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors"
          title="Zoom arrière"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// Icône de cluster — même esthétique que les pins (anneau + cercle plein,
// pas d'ombre lourde) plutôt que le style jaune/carré par défaut de
// leaflet.markercluster.
const createClusterIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 38 : count < 50 ? 46 : 54;
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
        border: 3px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-family: Arial, sans-serif;
        font-size: ${count < 100 ? 14 : 12}px;
        box-shadow: 0 2px 6px rgba(79, 70, 229, 0.35);
      ">${count}</div>
    `,
    className: 'custom-cluster-icon',
    iconSize: [size, size]
  });
};

const getVehicleEmoji = (vehicleType: string) => {
  switch (vehicleType) {
    case 'car':
      return '🚗';
    case 'motorcycle':
      return '🏍️';
    case 'scooter':
      return '🛴';
    case 'bike':
      return '🚲';
    case 'quad':
      return '🏎️';
    default:
      return '🚗';
  }
};

const OverviewMap: React.FC<OverviewMapProps> = ({
  userPosition,
  vehicles = [],
  onVehicleSelect,
  mode = 'book',
  onVehicleClick,
  actionLabel,
  markerColor = '#6366F1',
  getMarkerColor,
  route
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    // Masquer les attributions Leaflet et les contrôles par défaut
    const style = document.createElement('style');
    style.textContent = `
      .leaflet-bottom.leaflet-right {
        display: none !important;
      }
      .leaflet-control-zoom {
        display: none !important;
      }
      .leaflet-control-zoom-in,
      .leaflet-control-zoom-out {
        display: none !important;
      }
      .leaflet-control-container {
        display: none !important;
      }
      .leaflet-top.leaflet-right {
        display: none !important;
      }
      .leaflet-top.leaflet-left {
        display: none !important;
      }
      .leaflet-bottom.leaflet-left {
        display: none !important;
      }
      .leaflet-top {
        display: none !important;
      }
      .leaflet-control {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [position, setPosition] = useState<{ lat: number; lng: number }>({
    lat: 36.8065, // Tunis par défaut
    lng: 10.1815
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Si une position utilisateur est fournie, l'utiliser
    if (userPosition) {
      setPosition(userPosition);
      setLoading(false);
      return;
    }

    // Mode 'select' = back-office (Flotte, fiche utilisateur...) — on
    // affiche les véhicules, pas "autour de moi". Demander la position du
    // membre du staff connecté n'a pas de sens ici et ne fait que déclencher
    // une demande de permission navigateur inutile.
    if (mode === 'select') {
      setLoading(false);
      return;
    }

    // Sinon (mode 'book', client final), demander la géolocalisation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
          setLoading(false);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      console.log('Géolocalisation non supportée');
      setLoading(false);
    }
  }, [userPosition, mode]);

  // Écouter les changements de véhicule sélectionné depuis le parent
  useEffect(() => {
    if (onVehicleSelect) {
      setSelectedVehicle(onVehicleSelect);
    }
  }, [onVehicleSelect]);

  /* Squelette de carte : une surface au gabarit final avec quelques pastilles
     fantômes, plutôt qu'un spinner sur fond gris. */
  if (loading) {
    return (
      <div className="w-full h-full rounded-lg overflow-hidden shadow-lg">
        <div className="relative w-full h-full animate-pulse bg-gray-100">
          <div className="absolute left-[22%] top-[30%] h-7 w-7 rounded-full bg-gray-200" />
          <div className="absolute left-[58%] top-[24%] h-7 w-7 rounded-full bg-gray-200" />
          <div className="absolute left-[40%] top-[58%] h-7 w-7 rounded-full bg-gray-200" />
          <div className="absolute left-[72%] top-[64%] h-7 w-7 rounded-full bg-gray-200" />
          <div className="absolute left-4 top-4 h-8 w-32 rounded-lg bg-gray-200" />
          <div className="absolute right-4 top-4 h-8 w-8 rounded-lg bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-lg overflow-hidden shadow-lg">
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
        zoomControl={false}
        attributionControl={true}
      >
        <TileLayer attribution={BASEMAP_ATTRIBUTION} url={BASEMAP_URL} />
        
        <MapResizeHandler />

        {/* Contrôles de carte personnalisés */}
        <MapControls userPosition={position} showRecenter={mode !== 'select'} />
        {/* Marqueur "votre position" — sans objet en back-office, où la
            position affichée n'est pas celle du membre du staff mais un
            simple centre de carte. */}
        {mode !== 'select' && (
        <Marker
          position={[position.lat, position.lng]}
          icon={L.divIcon({
            className: 'custom-user-marker',
            html: `
              <div style="
                background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                width: 32px;
                height: 32px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 3px solid white;
                box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
              ">
                <div style="
                  transform: rotate(45deg);
                  color: white;
                  font-size: 16px;
                  font-weight: bold;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                ">📍</div>
              </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 32]
          })}
        >
          <Popup>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white text-sm">📍</span>
                </div>
                <h3 className="font-semibold text-gray-800">Votre position</h3>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Latitude:</span>
                  <span className="font-mono">{position.lat.toFixed(6)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Longitude:</span>
                  <span className="font-mono">{position.lng.toFixed(6)}</span>
                </div>
              </div>
              <div className="mt-3 pt-2 border-t border-gray-200">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                  Position active
                </span>
              </div>
            </div>
          </Popup>
        </Marker>
        )}

        {/* Marqueurs des véhicules — regroupés en clusters au dézoom */}
        <MarkerClusterGroup
          iconCreateFunction={createClusterIcon}
          maxClusterRadius={60}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
        >
        {vehicles
          .filter(vehicle => vehicle.latitude && vehicle.longitude &&
            !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude))
          .map((vehicle, index) => {
          const pinColor = getMarkerColor ? getMarkerColor(vehicle) : markerColor;
          return (
          <Marker
            key={`vehicle-${vehicle.id || index}-${vehicle.latitude}-${vehicle.longitude}`}
            position={[vehicle.latitude, vehicle.longitude]}
            icon={L.divIcon({
              className: 'custom-div-icon',
              html: `
                <div style="
                  position: relative;
                  width: 40px;
                  height: 40px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                  <!-- Anneau externe (le plus clair) -->
                  <div style="
                    position: absolute;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: ${hexToRgba(pinColor, 0.2)};
                    border: 2px solid ${hexToRgba(pinColor, 0.3)};
                  "></div>

                  <!-- Anneau intermédiaire -->
                  <div style="
                    position: absolute;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: ${hexToRgba(pinColor, 0.4)};
                    border: 2px solid ${hexToRgba(pinColor, 0.5)};
                  "></div>

                  <!-- Cercle central -->
                  <div style="
                    position: absolute;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: ${pinColor};
                    border: 2px solid white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  ">
                    <div style="
                      color: white;
                      font-size: 16px;
                      font-weight: bold;
                      font-family: Arial, sans-serif;
                    ">${getVehicleEmoji(vehicle.type)}</div>
                  </div>

                  ${mode === 'select' && vehicle.hourly_rate ? `
                  <!-- Pastille de prix -->
                  <div style="
                    position: absolute;
                    top: -4px;
                    right: -6px;
                    background: ${vehicle.is_available ? '#10B981' : '#EF4444'};
                    color: white;
                    font-size: 10px;
                    font-weight: 700;
                    font-family: Arial, sans-serif;
                    padding: 2px 6px;
                    border-radius: 999px;
                    border: 2px solid white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    white-space: nowrap;
                  ">${Math.round(vehicle.hourly_rate)}</div>
                  ` : ''}
                </div>
              `,
              iconSize: [40, 40],
              iconAnchor: [20, 40]
            })}
          >
            <Popup>
              {mode === 'select' ? (
                <div className="w-60 font-[Figtree]">
                  <div className="relative mb-3 h-28 overflow-hidden rounded-xl bg-[#F1F2F8]">
                    <PopupPhoto vehicle={vehicle} />
                    {vehicle.state && (
                      <StatusPill tone={vehicle.state.tone} className="absolute left-2 top-2 bg-white/95">{vehicle.state.label}</StatusPill>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <h3 className="min-w-0 flex-1 truncate text-[14px] font-extrabold text-[#1E293B]">{vehicle.title}</h3>
                    {vehicle.isPlatformOwned && (
                      <svg className="h-3.5 w-3.5 shrink-0 text-[#4F46E5]" fill="currentColor" viewBox="0 0 20 20" aria-label="Annonce Dingo">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                    {vehicle.plate && vehicle.plate !== vehicle.title && (
                      <span className="shrink-0 rounded-[5px] border-[1.5px] border-[#1E293B] px-1.5 text-[11px] font-extrabold leading-[16px] tracking-wide text-[#1E293B]">
                        {vehicle.plate}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[12px] text-[#6B7280]">
                    {[vehicle.typeLabel || vehicle.type, vehicle.year, vehicle.city].filter(Boolean).join(' · ')}
                  </div>

                  <div className="my-3 flex items-end gap-4 border-y border-[#F1F2F8] py-2.5">
                    {(vehicle.prices && vehicle.prices.length > 0 ? vehicle.prices : [{ value: '—', unit: 'tarif' }]).map((price) => (
                      <div key={price.unit}>
                        <div className="text-[14px] font-extrabold leading-tight text-[#1E293B]">{price.value}</div>
                        <div className="text-[11px] text-[#9CA3AF]">{price.unit}</div>
                      </div>
                    ))}
                    {vehicle.ownerName && (
                      <div className="ml-auto min-w-0 text-right">
                        <div className="truncate text-[12.5px] font-bold leading-tight text-[#1E293B]">{vehicle.ownerName}</div>
                        <div className="text-[11px] text-[#9CA3AF]">propriétaire</div>
                      </div>
                    )}
                  </div>

                  {onVehicleClick && (
                    <button
                      type="button"
                      onClick={() => onVehicleClick(vehicle)}
                      className="w-full rounded-xl bg-[#4F46E5] py-2.5 text-[12.5px] font-bold text-white transition-colors hover:bg-[#4338CA]"
                    >
                      {actionLabel || 'Voir les détails'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-2xl mb-2">{getVehicleEmoji(vehicle.type)}</div>
                  <h3 className="font-bold text-gray-800 mb-2 text-sm">{vehicle.title}</h3>
                  <div className="space-y-1 mb-3 text-xs text-gray-600">
                    <div className="font-semibold text-indigo-600">{vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}</div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500">4.8</span>
                        <svg className="w-3 h-3 text-yellow-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </div>
                      <span className="font-bold text-indigo-600">{vehicle.hourly_rate}€/h</span>
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                      vehicle.is_available
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                        vehicle.is_available ? 'bg-green-400' : 'bg-red-400'
                      }`}></div>
                      {vehicle.is_available ? 'Disponible' : 'Occupé'}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/booking', {
                        state: {
                          vehicle: {
                            id: vehicle.id,
                            title: vehicle.title,
                            type: vehicle.type,
                            price: `${vehicle.hourly_rate}€/h`,
                            distance: 'N/D',
                            status: vehicle.is_available ? 'disponible' : 'occupé',
                            icon: getVehicleEmoji(vehicle.type),
                            vehicle_type: vehicle.type,
                            hourly_rate: vehicle.hourly_rate
                          }
                        }
                      });
                    }}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 hover:scale-105 ${
                      vehicle.is_available
                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-700 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }`}
                    disabled={!vehicle.is_available}
                  >
                    {vehicle.is_available ? 'Réserver' : 'Indisponible'}
                  </button>
                </div>
              )}
            </Popup>
          </Marker>
          );
        })}
        </MarkerClusterGroup>

        <MapCenter position={position} />
        {selectedVehicle && <VehicleCenter vehicle={selectedVehicle} />}
        {route && <RouteOverlay route={route} color={markerColor} />}
      </MapContainer>
    </div>
  );
};

export default OverviewMap; 